import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faTrashRestore, faTrash, faTimes, faFolder, faLayerGroup, faCheckSquare, faStickyNote, faRedo, faClock,
    faStopwatch, faChartPie, faLock,
} from "@fortawesome/free-solid-svg-icons";
import { Button } from "../button/Button";
import { ConfirmationDialog } from "../confirmation-dialog/ConfirmationDialog";
import { loadFromStorage, saveToStorage, STORAGE_KEYS } from "../../utils/storageUtils";
import { Element, Collection, Folder, TrashItem } from "../../types";
import "./TrashBin.css";
import { useTranslation } from "react-i18next";

interface TrashBinProps {
    isOpen: boolean;
    onClose: () => void;
    onRestoreElement: (element: Element, sourceCollectionId: string) => void;
    onRestoreCollection: (collection: Collection, sourceFolderId: string) => void;
    onRestoreFolder: (folder: Folder) => void;
    folders: Folder[];
    areSecretCollectionsVisible: boolean;
    isSecretProtectionEnabled: boolean;
}

export const TrashBin: React.FC<TrashBinProps> = ({
    isOpen,
    onClose,
    onRestoreElement,
    onRestoreCollection,
    onRestoreFolder,
    folders,
    areSecretCollectionsVisible,
    isSecretProtectionEnabled,
}) => {
    const { t } = useTranslation();
    const [trashItems, setTrashItems] = useState<TrashItem[]>([]);
    const [itemToDelete, setItemToDelete] = useState<TrashItem | null>(null);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

    useEffect(() => {
        if (isOpen) {
            const trash = loadFromStorage<TrashItem[]>(STORAGE_KEYS.TRASH, []);
            if (trash) {
                const thirtyDaysAgo = new Date();
                thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

                const filteredTrash = trash.filter(item => {
                    const deletedDate = new Date(item.deletedAt);
                    return deletedDate > thirtyDaysAgo;
                });

                if (filteredTrash.length !== trash.length) {
                    saveToStorage(STORAGE_KEYS.TRASH, filteredTrash);
                }

                const sortedTrash = filteredTrash.sort((a, b) => 
                    new Date(b.deletedAt).getTime() - new Date(a.deletedAt).getTime()
                );

                setTrashItems(sortedTrash);
            }
        }
    }, [isOpen]);

    const handleRestoreElement = (item: TrashItem) => {
        if (item.element && item.sourceCollectionId) {
            onRestoreElement(item.element, item.sourceCollectionId);
            removeFromTrash(item.id);
        }
    };

    const handleRestoreCollection = (item: TrashItem) => {
        if (item.collection && item.sourceFolderId) {
            onRestoreCollection(item.collection, item.sourceFolderId);
            removeFromTrash(item.id);
        }
    };

    const handleRestoreFolder = (item: TrashItem) => {
        if (item.folder) {
            onRestoreFolder(item.folder);
            removeFromTrash(item.id);
        }
    };

    const handlePermanentDelete = () => {
        if (itemToDelete) {
            removeFromTrash(itemToDelete.id);
            setItemToDelete(null);
            setShowDeleteConfirmation(false);
        }
    };

    const removeFromTrash = (trashItemId: string) => {
        const updatedTrash = trashItems.filter(item => item.id !== trashItemId);
        setTrashItems(updatedTrash);
        saveToStorage(STORAGE_KEYS.TRASH, updatedTrash);
    };

    const formatDate = (isoString: string) => {
        const date = new Date(isoString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    };

    const getElementDisplayName = (element: Element) => {
        if (element.type === 'task') return element.title || element.task || 'Task';
        if (element.type === 'note') return element.title || 'Note';
        if (element.type === 'habit') return element.title || 'Habit';
        if (element.type === 'timer') return element.title || 'Timer';
        if (element.type === 'stopwatch') return element.title || 'Stopwatch';
        if (element.type === 'timeTracker') return element.title || 'Time tracker';
        return 'Item';
    };

    const getElementIcon = (element: Element) => {
        if (element.type === 'task') return faCheckSquare;
        if (element.type === 'note') return faStickyNote;
        if (element.type === 'habit') return faRedo;
        if (element.type === 'timer') return faClock;
        if (element.type === 'stopwatch') return faStopwatch;
        if (element.type === 'timeTracker') return faChartPie;
        return faTrash;
    };

    const getElementTypeLabel = (element: Element) => {
        if (element.type === 'task') return t('trashBin.task');
        if (element.type === 'note') return t('trashBin.note');
        if (element.type === 'habit') return t('trashBin.habit');
        if (element.type === 'timer') return t('trashBin.timer');
        if (element.type === 'stopwatch') return t('trashBin.stopwatch');
        if (element.type === 'timeTracker') return t('trashBin.timeTracker');
        return 'Item';
    };

    const getFolderDisplayName = (folder: Folder) => {
        return folder.name || 'Folder';
    };

    const getCollectionName = (collectionId: string, collectionName?: string) => {
        if (collectionName) return collectionName;
        for (const folder of folders) {
            const collection = (folder.collections || []).find((c: Collection) => c.id === collectionId);
            if (collection) return collection.name;
        }
        return 'Unknown Collection';
    };

    const getFolderName = (folderId: string, folderName?: string) => {
        if (folderName) return folderName;
        const folder = folders.find((f: Folder) => f.id === folderId);
        return folder ? folder.name : 'Unknown Folder';
    };

    const isSecretTrashItem = (item: TrashItem): boolean => {
        if (item.secret || item.collection?.secret) return true;
        return Boolean(item.folder?.collections.some((collection) => collection.secret));
    };

    const visibleTrashItems = !isSecretProtectionEnabled || areSecretCollectionsVisible
        ? trashItems
        : trashItems.filter((item) => !isSecretTrashItem(item));

    if (!isOpen) return null;

    return (
        <>
            <div className="trash-bin-overlay" onClick={(e) => { e.stopPropagation(); onClose(); }}>
                <div className="trash-bin" onClick={(e) => e.stopPropagation()}>
                    <div className="trash-bin-header">
                        <h2>{t('trashBin.title')}</h2>
                        <button className="close-button" onClick={onClose}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                    <div className="trash-bin-info">
                        <small>{t('trashBin.autoDeleteInfo')}</small>
                    </div>
                    <div className="trash-bin-content">
                        {visibleTrashItems.length === 0 ? (
                            <div className="trash-bin-empty">
                                <p>{t('trashBin.empty')}</p>
                            </div>
                        ) : (
                            <div className="trash-items-list">
                                {visibleTrashItems.map((item) => (
                                    <div key={item.id} className="trash-item">
                                        <div className="trash-item-info">
                                            <div className="trash-item-icon">
                                                {item.type === 'folder' ? (
                                                    <FontAwesomeIcon icon={faFolder} />
                                                ) : item.type === 'collection' ? (
                                                    <FontAwesomeIcon icon={faLayerGroup} />
                                                ) : item.element ? (
                                                    <FontAwesomeIcon icon={getElementIcon(item.element)} />
                                                ) : (
                                                    <FontAwesomeIcon icon={faTrash} />
                                                )}
                                            </div>
                                            <div className="trash-item-details">
                                                <div className="trash-item-header">
                                                    <div className="trash-item-type">
                                                        {item.type === 'folder'
                                                            ? t('trashBin.folder')
                                                            : item.type === 'collection'
                                                            ? t('trashBin.collection')
                                                            : item.element ? getElementTypeLabel(item.element) : 'Item'
                                                        }
                                                    </div>
                                                    <div className="trash-item-name">
                                                        <span>
                                                            {item.type === 'folder'
                                                                ? getFolderDisplayName(item.folder!)
                                                                : item.type === 'collection'
                                                                ? item.collection?.name || 'Collection'
                                                                : item.element ? getElementDisplayName(item.element) : 'Item'
                                                            }
                                                        </span>
                                                        {isSecretTrashItem(item) && (
                                                            <FontAwesomeIcon
                                                                icon={faLock}
                                                                title={t('secretCollections.secret')}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="trash-item-breadcrumb">
                                                    {item.type === 'folder'
                                                        ? null
                                                        : item.type === 'collection'
                                                        ? (
                                                            <span className="breadcrumb-item">{getFolderName(item.sourceFolderId || '', item.sourceFolderName)}</span>
                                                        )
                                                        : item.element ? (
                                                            <>
                                                                <span className="breadcrumb-item">{getFolderName(item.sourceFolderId || '', item.sourceFolderName)}</span>
                                                                <span className="breadcrumb-separator">/</span>
                                                                <span className="breadcrumb-item">{getCollectionName(item.sourceCollectionId || '', item.sourceCollectionName)}</span>
                                                            </>
                                                        ) : null
                                                    }
                                                </div>
                                                <div className="trash-item-date">
                                                    {t('trashBin.deletedAt')}: {formatDate(item.deletedAt)}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="trash-item-actions">
                                            <Button
                                                text=""
                                                icon={faTrashRestore}
                                                status="primary"
                                                type="button"
                                                onClick={() => {
                                                    if (item.type === 'folder') {
                                                        handleRestoreFolder(item);
                                                    } else if (item.type === 'collection') {
                                                        handleRestoreCollection(item);
                                                    } else {
                                                        handleRestoreElement(item);
                                                    }
                                                }}
                                                ariaLabel={t('trashBin.restore')}
                                            />
                                            <Button
                                                text=""
                                                icon={faTrash}
                                                status="danger"
                                                type="button"
                                                onClick={() => {
                                                    setItemToDelete(item);
                                                    setShowDeleteConfirmation(true);
                                                }}
                                                ariaLabel={t('trashBin.permanentlyDelete')}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {showDeleteConfirmation && itemToDelete && (
                <ConfirmationDialog
                    title={t('trashBin.permanentlyDeleteTitle')}
                    message={t('trashBin.permanentlyDeleteConfirm')}
                    onConfirm={handlePermanentDelete}
                    onCancel={() => {
                        setItemToDelete(null);
                        setShowDeleteConfirmation(false);
                    }}
                    isDanger={true}
                />
            )}
        </>
    );
};
